import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CashierSection, ContextStatus, DepositPhase } from "../payments/PaymentsReducer";
import { AppState } from "../RootReducer";
import { t } from "../i18n";
import { showCashier } from "../payments/DepositActions";
import { fetchBalance } from "../user/UserActions";
import { getActiveCurrency, getVisibleCurrencies } from "../selectors/user-selectors";
import ChooseMethod from "../ui/deposit/ChooseMethod";
import { Spinner } from "react-bootstrap";
import ProfileMetamapComponent from "../profilepage/ProfileMetamapComponent";
import { PaymentLoading } from "./PaymentLoading";
import "../ui/deposit/styles/Deposit.scss";
import "./styles/Method.scss";
import { reset } from "../payments/PaymentsActions";
import { PaymentMethodBackButton } from "./PaymentMethodBackButton";
import { prettyUrl } from "../utils/UrlUtils";
import CryptoDepositMethods from "./CryptoMethods/CryptoDepositMethods";
import ChangeIcon from "../utils/changeIcon";
import ChangeCurrency from "./ChangeCurrency";
import DepositProviderSpecific from "./DepositProviderSpecific";

const Deposit = () => {
  const [showCurrency, setShowCurrency] = useState(false);
  const dispatch = useDispatch();
  const deposit = useSelector((state: AppState) => state.payments.deposit);
  const user = useSelector((state: AppState) => state.user.user);
  const currencies = useSelector((state: AppState) => getVisibleCurrencies(state));
  const activeSiteCurrency = useSelector((state: AppState) => getActiveCurrency(state));
  const settings = useSelector((state: AppState) => state.settings.settings);

  const handleClickStartPlaying = () => {
    dispatch(fetchBalance() as any);
    dispatch(showCashier(false, CashierSection.NONE));
  };

  if (!deposit || !activeSiteCurrency || !currencies) {
    return <PaymentLoading />;
  }

  const phase = deposit.phase;
  const dcStatus = deposit.depositContextStatus;
  const method = deposit.activeMethod && deposit.activeMethod.name;

  const isKycRequired = deposit.activeMethod?.requireKycOnDeposit;
  const isUserKycVerified = user?.kycVerified ?? false;
  const isKycRequirementsNotMet = isKycRequired && !isUserKycVerified;
  const userHasChosenMethod = deposit.activeMethod || deposit.group;
  const onlyOneMethod = deposit.methods?.[deposit?.activeCurrency ?? ""]?.length === 1;
  const isCryptoCurrency = activeSiteCurrency?.cryptoCurrency;
  const hasMultipleCurrencies = user?.currencies?.length && user.currencies.length > 1;

  return (
    <div className={`cashier-inner-container deposit deposit-wrapper method ${method ? prettyUrl(method) : ""}`}>
      <div className="content-wrapper">
        <div className="paymentSelectorButton">
          {/* If current currency is crypto we assume there is no FIAT methods and thus the user can change method in the crypto deposit methods. */}
          {!showCurrency && userHasChosenMethod && !onlyOneMethod && !isCryptoCurrency && (
            <PaymentMethodBackButton onClick={() => dispatch(reset(activeSiteCurrency.code) as any)}>
              {t(
                `payments.method.${deposit.activeMethod?.code}.name`,
                undefined,
                deposit.group ? t(`payments.method.${deposit.group}.name`) || deposit.group : deposit.activeMethod?.name,
              )}
            </PaymentMethodBackButton>
          )}
        </div>

        <div className="deposit-container method">
          {!isKycRequirementsNotMet &&
            (phase === DepositPhase.CHOOSE_METHOD || !deposit.activeMethod) &&
            !deposit.group &&
            !isCryptoCurrency &&
            !showCurrency && (
              <>
                {dcStatus === ContextStatus.OK && <ChooseMethod />}

                {dcStatus === ContextStatus.IN_PROGRESS && (
                  <div className="chooseMethod">
                    <div className="wrapper">
                      <div style={{ width: "100%", padding: "50px", textAlign: "center" }}>
                        <Spinner animation={"border"} />
                      </div>
                    </div>
                  </div>
                )}

                {dcStatus === ContextStatus.ERROR && (
                  <div className="chooseMethod">
                    <div className="wrapper">
                      <div className={"col-12"}>
                        <div className={"col-12"}>
                          <div className={"alert alert-danger"}>{t("deposit.context-error")}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

          {!showCurrency && (
            <>
              {deposit.group && <CryptoDepositMethods isKycRequirementsNotMet={isKycRequirementsNotMet} groupName={deposit.group} />}
              {isCryptoCurrency && !deposit.group && <CryptoDepositMethods isKycRequirementsNotMet={isKycRequirementsNotMet} />}
            </>
          )}

          <div className="choose-amount">
            {!isKycRequirementsNotMet && !showCurrency && !deposit.group && !isCryptoCurrency && (
              <>
                {deposit.activeMethod && deposit.activeMethod.provider && !deposit.group && !isCryptoCurrency && (
                  <DepositProviderSpecific
                    provider={deposit.activeMethod.provider}
                    deposit={deposit}
                    handleClickStartPlaying={handleClickStartPlaying}
                    code={deposit.activeMethod.code}
                    settings={settings}
                    user={user}
                  />
                )}
              </>
            )}
            {showCurrency && <ChangeCurrency onClose={() => setShowCurrency(false)} />}
            {isKycRequirementsNotMet && !showCurrency && (
              <>
                <div className={"alert alert-danger"}>{t("payments.deposit-kyc-required")}</div>
                <ProfileMetamapComponent />
              </>
            )}
          </div>
          {!showCurrency && hasMultipleCurrencies && (
            <span className={"change-currency-button"} onClick={() => setShowCurrency(true)}>
              <ChangeIcon className="change" />
              {t("payments.changeCurrency")}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default Deposit;
